import React, { ReactNode } from "react";

import ModalContainer from "../ModalContainer";
import { Actions, Body, CancelButton, ConfirmButton } from "./style";

interface ConfirmationModalProps {
  isOpened: boolean;
  title: string;
  /** The question's detail: what the action does and to what. */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  /** Also runs for the close X and a click outside. */
  onCancel: () => void;
  /** Disables both actions while the confirmed action is in flight. */
  isConfirming?: boolean;
  testId?: string;
}

/* The ModalContainer title is a 50px display heading; a question reads better
 * smaller. The top margin keeps it clear of the tray's close X on phones. */
const TITLE_STYLE = ["font-size: 32px;", "margin-top: 16px;"];

/**
 * A yes/no dialog for an action worth a second look: the question as the
 * title, its consequences in the body, the action first and the way out below
 * it. Mounts nothing while closed, so callers can render it unconditionally.
 */
const ConfirmationModal = ({
  isOpened,
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isConfirming = false,
  testId = "confirmation-modal",
}: ConfirmationModalProps) => {
  if (!isOpened) return null;

  return (
    <ModalContainer
      isOpened
      outsideClickHandler={onCancel}
      title={title}
      // @ts-expect-error ModalContainer is JS and its `titleStyle = []` default infers as never[].
      titleStyle={TITLE_STYLE}
      isScrollLockEnabled
      fullViewMask
      isModalMdUpHeightAuto
    >
      <div role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
        <Body>{children}</Body>
        <Actions>
          <ConfirmButton type="button" full onClick={onConfirm} disabled={isConfirming}>
            {confirmLabel}
          </ConfirmButton>
          <CancelButton type="button" full ghost onClick={onCancel} disabled={isConfirming}>
            {cancelLabel}
          </CancelButton>
        </Actions>
      </div>
    </ModalContainer>
  );
};

export default ConfirmationModal;
